import { FileSignature, FileText, Link2, MessageSquare, Settings2 } from 'lucide-react'
import { FinanceClosedScopeSection } from '@/components/Financeiro/FinanceClosedScopeSection'
import { hasClosedScopeContent, buildClosedScope } from '@/components/Financeiro/financeClosedScope'
import { FinanceG5ContratacaoSection } from '@/components/Financeiro/FinanceG5ContratacaoSection'
import { FinanceC4ContractDeliverablesSection } from '@/components/Financeiro/FinanceC4ContractDeliverablesSection'
import { FinanceDetailSectionCard } from '@/components/Financeiro/FinanceDetailSectionCard'
import { FinanceRequestClosedDealSummary } from '@/components/Financeiro/FinanceRequestClosedDealSummary'
import { FinanceRequestCommentsPanel } from '@/components/Financeiro/FinanceRequestCommentsPanel'
import { FinanceRequestContractSection } from '@/components/Financeiro/FinanceRequestContractSection'
import { FinanceRequestFormFields } from '@/components/Financeiro/FinanceRequestFormFields'
import { FinanceRequestQueuePanel } from '@/components/Financeiro/FinanceRequestQueuePanel'
import { getDisplayFormEntries } from '@/components/Financeiro/financeRequestFormDisplay'
import { showSignedContractSection } from '@/components/Financeiro/financeRequestStatus.util'
import type { FinanceAssignee, FinanceRequestDetail } from '@/services/financeService'

const C4_SUMMARY_FIELD_KEYS = new Set([
  'summary',
  'description',
  'cnpj',
  'razaoSocial',
  'email',
  'endereco',
  'plataforma',
  'servicosContratados',
  'valorTotal',
  'valorMensal',
  'valorSetup',
  'condicaoPagamento',
  'closerNome',
  'closerEmail',
  'proposta',
  'contractDeliveryMode',
  'contractDeliveryLabel',
  'contractSignatureLink',
])

const G5_INTERNAL_FORM_KEYS = new Set(['pjInviteId', 'pjInviteUrl', 'pjInviteExpiresAt', 'pjAgradaId'])

function g5DisplayFormData(formData: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(formData).filter(
      ([key, value]) => !G5_INTERNAL_FORM_KEYS.has(key) && value != null && value !== '',
    ),
  )
}

function c4ExtraFormData(formData: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(formData).filter(
      ([key, value]) => !C4_SUMMARY_FIELD_KEYS.has(key) && value != null && value !== '',
    ),
  )
}

type Props = {
  detail: FinanceRequestDetail
  canManageQueue: boolean
  canGenerateG5Invite?: boolean
  canViewFinancialValues: boolean
  canViewClosedScope: boolean
  assignees: FinanceAssignee[]
  onUpdateStatus: (status: string, approvalPendingFrom?: string) => Promise<boolean>
  onAssign: (assigneeId: string | null) => Promise<boolean>
  onAddComment: (body: string, isInternal?: boolean, attachments?: string[]) => Promise<boolean>
  onReload?: () => void
}

export function FinanceRequestDetailContent({
  detail,
  canManageQueue,
  canGenerateG5Invite = false,
  canViewFinancialValues,
  canViewClosedScope,
  assignees,
  onUpdateStatus,
  onAssign,
  onAddComment,
  onReload,
}: Props) {
  const formData =
    detail.formData && typeof detail.formData === 'object'
      ? (detail.formData as unknown as Record<string, unknown>)
      : {}
  const projectSnapshot = detail.project?.proposalSnapshots?.[0] ?? null
  const { propostaSnapshot: formProposta } = getDisplayFormEntries(formData)
  const proposalSnapshot = projectSnapshot ?? formProposta
  const extraC4Form = c4ExtraFormData(formData)
  const closedScope = buildClosedScope({
    formData,
    snapshot: proposalSnapshot,
    includeValues: canViewFinancialValues,
  })
  const showClosedScopeSection =
    detail.type === 'C4_CONTRATO_AGRADA' &&
    canViewClosedScope &&
    (hasClosedScopeContent(closedScope) || !canViewFinancialValues)

  const signedContracts = (detail.project?.contracts ?? []).filter((c) => c.type !== 'nao_assinado')
  const showSignedContract = showSignedContractSection(detail)

  return (
    <div className='grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]'>
      <div className='min-w-0 space-y-4'>
        <FinanceRequestClosedDealSummary
          detail={detail}
          canViewFinancialValues={canViewFinancialValues}
        />

        {showClosedScopeSection && (
          <FinanceClosedScopeSection
            formData={formData}
            snapshot={proposalSnapshot}
            empresaNome={detail.project?.empresa?.nome}
            canViewFinancialValues={canViewFinancialValues}
            showEmptyState={!canViewFinancialValues}
            financeProtocol={detail.protocol}
          />
        )}

        {detail.type === 'C4_CONTRATO_AGRADA' && (
          <FinanceDetailSectionCard title='Entrega do contrato' icon={Link2} accent='#6366f1'>
            <FinanceC4ContractDeliverablesSection
              detail={detail}
              canManage={canManageQueue}
              onReload={onReload}
            />
          </FinanceDetailSectionCard>
        )}

        {detail.type === 'G5_CONTRATACAO_PJ' && (
          <FinanceG5ContratacaoSection
            detail={detail}
            canGenerateG5Invite={canGenerateG5Invite}
            onReload={() => onReload?.()}
          />
        )}

        {showSignedContract && (
          <FinanceDetailSectionCard title='Contrato assinado' icon={FileSignature} accent='#ef4444'>
            <FinanceRequestContractSection
              projectId={detail.project!.id}
              contracts={signedContracts}
              canUpload={canManageQueue && detail.status === 'CONTRATO_ASSINADO'}
              onUploaded={() => onReload?.()}
            />
          </FinanceDetailSectionCard>
        )}

        {detail.type === 'C4_CONTRATO_AGRADA'
          ? Object.keys(extraC4Form).length > 0 && (
              <FinanceDetailSectionCard title='Dados complementares' icon={FileText} accent='#8b5cf6'>
                <FinanceRequestFormFields formData={extraC4Form} showHeading={false} />
              </FinanceDetailSectionCard>
            )
          : detail.type === 'G5_CONTRATACAO_PJ'
            ? Object.keys(g5DisplayFormData(formData)).length > 0 && (
                <FinanceDetailSectionCard title='Dados enviados' icon={FileText} accent='#8b5cf6'>
                  <FinanceRequestFormFields formData={g5DisplayFormData(formData)} />
                </FinanceDetailSectionCard>
              )
            : Object.keys(formData).length > 0 && (
              <FinanceDetailSectionCard title='Dados enviados' icon={FileText} accent='#8b5cf6'>
                <FinanceRequestFormFields formData={formData} />
              </FinanceDetailSectionCard>
            )}

        <FinanceDetailSectionCard
          title='Comentários'
          icon={MessageSquare}
          accent='#ef4444'
          className='xl:hidden'
        >
          <FinanceRequestCommentsPanel detail={detail} canManageQueue={canManageQueue} onAddComment={onAddComment} />
        </FinanceDetailSectionCard>
      </div>

      <aside className='min-w-0 space-y-4 xl:sticky xl:top-4 xl:self-start'>
        <FinanceDetailSectionCard title='Triagem da fila' icon={Settings2} accent='#ef4444'>
          <FinanceRequestQueuePanel
            detail={detail}
            canManageQueue={canManageQueue}
            assignees={assignees}
            onUpdateStatus={onUpdateStatus}
            onAssign={onAssign}
          />
        </FinanceDetailSectionCard>

        <FinanceDetailSectionCard
          title='Comentários'
          icon={MessageSquare}
          accent='#6366f1'
          className='hidden xl:block'
        >
          <FinanceRequestCommentsPanel
            detail={detail}
            canManageQueue={canManageQueue}
            onAddComment={onAddComment}
            compact
          />
        </FinanceDetailSectionCard>
      </aside>
    </div>
  )
}
